// Imperative camera access for UI outside the map tree (list rows, controls,
// search). The MapView attaches the MapLibre instance on load.
import type { Map as MaplibreMap, PaddingOptions } from 'maplibre-gl';
import { CAMERA, MAP } from '../config/constants';
import { bboxToRegion, regionToBBox, type LatLng, type Region } from '../lib/geo';

let map: MaplibreMap | null = null;

export const mapController = {
  attach(m: MaplibreMap) {
    map = m;
    // Exposed for QA scripts and the browser console.
    (window as unknown as { __enkiMap?: MaplibreMap }).__enkiMap = m;
  },
  detach(m: MaplibreMap) {
    if (map === m) map = null;
  },
  get instance(): MaplibreMap | null {
    return map;
  },

  /** Visible region from the current bounds (MKCoordinateRegion equivalent). */
  currentRegion(): Region | null {
    if (!map) return null;
    const b = map.getBounds();
    return bboxToRegion({ minLat: b.getSouth(), maxLat: b.getNorth(), minLon: b.getWest(), maxLon: b.getEast() });
  },

  /** `position = .region(...)`: fit a region, respecting the panel padding. */
  showRegion(region: Region, options: { animate?: boolean; maxZoom?: number } = {}) {
    if (!map) return;
    const b = regionToBBox(region);
    map.fitBounds(
      [
        [b.minLon, b.minLat],
        [b.maxLon, b.maxLat],
      ],
      { duration: options.animate === false ? 0 : 700, maxZoom: options.maxZoom ?? MAP.maxZoom, padding: 0 },
    );
  },

  /**
   * Alert icon tap (CombinedMapView.handleAnnotationTap): zoom in 4x (span / 4,
   * two zoom levels). For a single alert, offset the center south by a quarter
   * of the new span so the detail does not cover the icon.
   */
  zoomInOn(center: LatLng, offsetSouth: boolean) {
    if (!map) return;
    const current = this.currentRegion();
    const newZoom = Math.min(MAP.maxZoom, map.getZoom() + 2);
    let lat = center.lat;
    if (offsetSouth && current) {
      const newSpan = current.latDelta / 4;
      lat = center.lat - newSpan / 4;
    }
    map.easeTo({ center: [center.lng, lat], zoom: newZoom, duration: 600 });
  },

  setPadding(padding: PaddingOptions) {
    map?.setPadding(padding);
  },

  defaultDistance: CAMERA.defaultDistance,
};
